import React, { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions, auth } from '../firebaseConfig';
import { dataService } from '../services/dataService';
import { useFeatureFlags } from '../contexts/FeatureFlagContext';
import TestAppPanel from './TestAppPanel';

const callClaude    = httpsCallable(functions, 'claude');
const setClaudeKey  = httpsCallable(functions, 'setClaudeApiKey');
const sendTestSms   = httpsCallable(functions, 'sendTestSms');

// ── SMS test section (admin) ──────────────────────────────────────────────
function SmsTestSection() {
  const [phone, setPhone] = useState('');
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState(null);

  const send = async () => {
    setSending(true); setMsg(null);
    try {
      await sendTestSms({ to: phone.trim() });
      setMsg({ ok: true, text: 'Sent — check your phone.' });
    } catch (e) {
      setMsg({ ok: false, text: e?.message || 'Failed to send.' });
    }
    setSending(false);
  };

  return (
    <div className="settings-section">
      <h3>SMS (Twilio)</h3>
      <p className="settings-desc">Send a one-off test SMS to confirm the Twilio channel works. On a trial account the number must be verified in Twilio first.</p>
      <div className="ai-key-row">
        <input
          type="tel"
          className="ai-key-input"
          placeholder="+44 7700 900000"
          value={phone}
          onChange={e => setPhone(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && phone.trim() && send()}
        />
        <button className="btn-primary" onClick={send} disabled={sending || !phone.trim()}>
          {sending ? 'Sending…' : 'Send test SMS'}
        </button>
      </div>
      {msg && <span className={`settings-msg ${msg.ok ? 'ok' : 'fail'}`}>{msg.ok ? '✓' : '✗'} {msg.text}</span>}
    </div>
  );
}

// ── Profile section ────────────────────────────────────────────────────────
function ProfileSection({ user }) {
  const [username, setUsername] = useState('');
  const [original, setOriginal] = useState('');
  const [saving,   setSaving]   = useState(false);
  const [msg,      setMsg]      = useState('');

  useEffect(() => {
    if (!user) return;
    dataService.getUserData(user.uid).then(d => {
      const name = d?.username || user.email.split('@')[0];
      setUsername(name); setOriginal(name);
    });
  }, [user]);

  const save = async () => {
    if (!username.trim() || username === original) return;
    setSaving(true);
    try {
      await dataService.createOrUpdateUser(user.uid, user.email, username);
      setOriginal(username);
      setMsg('Saved.');
    } catch { setMsg('Failed to save.'); }
    setSaving(false);
    setTimeout(() => setMsg(''), 3000);
  };

  return (
    <div className="settings-section">
      <h3>Profile</h3>
      <div className="settings-row">
        <div className="settings-field">
          <label>Display name</label>
          <div className="settings-input-row">
            <input
              value={username}
              onChange={e => setUsername(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && save()}
              placeholder="Your name"
            />
            <button className="btn-primary" onClick={save} disabled={saving || !username.trim() || username === original}>
              {saving ? 'Saving…' : 'Update'}
            </button>
          </div>
          {msg && <span className={`settings-msg ${msg === 'Saved.' ? 'ok' : 'fail'}`}>{msg}</span>}
        </div>
        <div className="settings-field">
          <label>Email</label>
          <div className="settings-static">{user?.email}</div>
        </div>
      </div>
    </div>
  );
}

// ── Appearance section ─────────────────────────────────────────────────────
function AppearanceSection() {
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'light');

  const apply = (t) => {
    setTheme(t);
    localStorage.setItem('theme', t);
    document.documentElement.setAttribute('data-theme', t);
  };

  return (
    <div className="settings-section">
      <h3>Appearance</h3>
      <div className="theme-options">
        {[
          { id: 'light', icon: '☀️', label: 'Light' },
          { id: 'dark',  icon: '🌙', label: 'Dark'  },
        ].map(t => (
          <button
            key={t.id}
            className={`theme-option ${theme === t.id ? 'active' : ''}`}
            onClick={() => apply(t.id)}
          >
            <span className="theme-option-icon">{t.icon}</span>
            <span>{t.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ── AI section ─────────────────────────────────────────────────────────────
function AISection({ isAdmin }) {
  const { aiEnabled, loading: aiLoading, setAiEnabled } = useFeatureFlags();
  const [aiError, setAiError] = useState('');
  const [testing,    setTesting]    = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [apiKey,     setApiKey]     = useState('');
  const [saving,     setSaving]     = useState(false);
  const [keyMsg,     setKeyMsg]     = useState('');

  const test = async () => {
    setTesting(true); setTestResult(null);
    try {
      const r = await callClaude({
        messages: [{ role: 'user', content: 'Reply with just the word "working"' }],
        model: 'claude-haiku-4-5-20251001', max_tokens: 20,
        workspaceId: 'settings-test', purpose: 'connection_test',
      });
      const text = r?.data?.content?.[0]?.text ?? '(no response)';
      setTestResult({ ok: true, text: `AI responded: "${text}"` });
    } catch (e) {
      setTestResult({ ok: false, text: `Error: ${e?.message || 'Unknown'}` });
    }
    setTesting(false);
  };

  const saveKey = async () => {
    if (!apiKey.startsWith('sk-ant-')) { setKeyMsg('Key must start with sk-ant-'); return; }
    setSaving(true); setKeyMsg('');
    try {
      await setClaudeKey({ apiKey });
      setKeyMsg('Saved.'); setApiKey('');
    } catch (e) { setKeyMsg(`Error: ${e?.message}`); }
    setSaving(false);
    setTimeout(() => setKeyMsg(''), 4000);
  };

  return (
    <div className="settings-section">
      <h3>AI</h3>
      <p className="settings-desc">
        This is a <strong>workspace</strong> setting — switching it off switches AI off for
        everyone here, on every device. It used to be remembered per browser, which is why it
        could read as off on your phone after being switched on at your desk.
      </p>
      <p className="settings-desc">
        Buzz AI and the node generators run on Projectly's own key, set here by an admin.
      </p>
      <p className="settings-desc">
        <strong>Agents do not use this key.</strong> They run on your workspace's own Anthropic
        account, added under <strong>Connectors → Claude (Anthropic)</strong>, so their usage is
        billed to you. If you have already connected one there, you do not need to add anything here.
      </p>

      <div className="ai-toggle-row">
        <div className="ai-toggle-info">
          <span className="ai-toggle-label">Enable AI features</span>
          <span className="ai-toggle-hint">
            {aiEnabled
              ? 'AI features are active — agents, chats, Buzz AI, and generators are available.'
              : 'AI features are disabled — the app runs in automation-only mode.'}
          </span>
        </div>
        <button
          className={`settings-toggle${aiEnabled ? ' on' : ''}`}
          disabled={aiLoading}
          onClick={async () => {
            setAiError('');
            try { await setAiEnabled(!aiEnabled); }
            catch (e) {
              // Say so rather than leaving a switch showing a state the server
              // does not hold — which is the bug this replaced.
              setAiError(e?.message?.includes('permission')
                ? 'You do not have permission to change this.'
                : 'Could not save that. The switch has been put back.');
            }
          }}
          role="switch"
          aria-checked={aiEnabled}
        >
          <span className="settings-toggle-knob" />
        </button>
      </div>
      {aiError && <p className="settings-desc" style={{ color: 'var(--accent-danger)' }}>{aiError}</p>}

      <div style={!aiEnabled ? { opacity: 0.4, pointerEvents: 'none' } : undefined}>
        <div className="ai-test-row">
          <button className="btn-secondary" onClick={test} disabled={testing}>
            {testing ? 'Testing…' : 'Test AI connection'}
          </button>
          {testResult && (
            <span className={`ai-test-result ${testResult.ok ? 'ok' : 'fail'}`}>
              {testResult.ok ? '✓' : '✗'} {testResult.text}
            </span>
          )}
        </div>

        {isAdmin && (
          <div className="ai-key-section">
            <label className="ai-key-label">Projectly's Claude API key <span className="panel-label-hint">— platform-wide, not for agents</span></label>
            <div className="ai-key-row">
              <input
                type="password"
                className="ai-key-input"
                placeholder="sk-ant-api03-…"
                value={apiKey}
                onChange={e => setApiKey(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && saveKey()}
              />
              <button className="btn-primary" onClick={saveKey} disabled={saving || !apiKey.trim()}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
            {keyMsg && <span className={`settings-msg ${keyMsg === 'Saved.' ? 'ok' : 'fail'}`}>{keyMsg}</span>}
            <span className="settings-hint">Stored encrypted server-side — never exposed to the browser.</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Notifications section ─────────────────────────────────────────────────
function NotificationsSection({ user }) {
  const [phone, setPhone]       = useState('');
  const [channels, setChannels] = useState({ inbox: true, email: false, whatsapp: false });
  const [types, setTypes]       = useState({ all: true, approvals: false, errors: false });
  const [saving, setSaving]     = useState(false);
  const [msg, setMsg]           = useState('');
  const [loaded, setLoaded]     = useState(false);

  useEffect(() => {
    if (!user) return;
    dataService.getUserData(user.uid).then(d => {
      if (d?.contactInfo?.phone) setPhone(d.contactInfo.phone);
      if (d?.notificationPreferences?.channels) setChannels(d.notificationPreferences.channels);
      if (d?.notificationPreferences?.types) setTypes(d.notificationPreferences.types);
      setLoaded(true);
    });
  }, [user]);

  const toggleChannel = (key) => setChannels(prev => ({ ...prev, [key]: !prev[key] }));
  const toggleType = (key) => {
    if (key === 'all') {
      setTypes({ all: true, approvals: false, errors: false });
    } else {
      setTypes(prev => {
        const next = { ...prev, [key]: !prev[key], all: false };
        if (!next.approvals && !next.errors) next.all = true;
        return next;
      });
    }
  };

  const save = async () => {
    setSaving(true); setMsg('');
    try {
      await dataService.updateContactInfo(user.uid, { phone: phone.trim() });
      await dataService.updateNotificationPreferences(user.uid, { channels, types });
      setMsg('Saved.');
    } catch { setMsg('Failed to save.'); }
    setSaving(false);
    setTimeout(() => setMsg(''), 3000);
  };

  if (!loaded) return null;

  return (
    <div className="settings-section">
      <h3>Notifications</h3>
      <p className="settings-desc">Choose how and when you want to be notified about pipeline events, approvals, and errors.</p>

      <div className="settings-field" style={{ marginBottom: 16 }}>
        <label>Phone / WhatsApp number</label>
        <input
          value={phone}
          onChange={e => setPhone(e.target.value)}
          placeholder="+44 7700 900000"
          style={{ maxWidth: 280 }}
        />
        <span className="settings-hint">Used for WhatsApp notifications. Include country code.</span>
      </div>

      <div style={{ marginBottom: 16 }}>
        <label className="settings-label-bold">Notification channels</label>
        <div className="notif-toggles">
          {[
            { key: 'inbox',    label: 'In-app inbox',    hint: 'Always on — messages appear in your inbox' },
            { key: 'email',    label: 'Email',           hint: 'Send a copy to your login email' },
            { key: 'whatsapp', label: 'WhatsApp',        hint: 'Receive via WhatsApp (requires phone number)' },
          ].map(ch => (
            <div className="notif-toggle-row" key={ch.key}>
              <div className="notif-toggle-info">
                <span className="notif-toggle-label">{ch.label}</span>
                <span className="notif-toggle-hint">{ch.hint}</span>
              </div>
              <button
                className={`settings-toggle${channels[ch.key] ? ' on' : ''}${ch.key === 'inbox' ? ' disabled' : ''}`}
                onClick={() => ch.key !== 'inbox' && toggleChannel(ch.key)}
                role="switch"
                aria-checked={channels[ch.key]}
                disabled={ch.key === 'inbox'}
              >
                <span className="settings-toggle-knob" />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <label className="settings-label-bold">Notify me about</label>
        <div className="notif-toggles">
          {[
            { key: 'all',       label: 'Everything',     hint: 'All pipeline events, approvals, and errors' },
            { key: 'approvals', label: 'Approvals only', hint: 'Gate approvals and decisions' },
            { key: 'errors',    label: 'Errors only',    hint: 'Pipeline failures and action errors' },
          ].map(t => (
            <div className="notif-toggle-row" key={t.key}>
              <div className="notif-toggle-info">
                <span className="notif-toggle-label">{t.label}</span>
                <span className="notif-toggle-hint">{t.hint}</span>
              </div>
              <button
                className={`settings-toggle${types[t.key] ? ' on' : ''}`}
                onClick={() => toggleType(t.key)}
                role="switch"
                aria-checked={types[t.key]}
              >
                <span className="settings-toggle-knob" />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="settings-input-row">
        <button className="btn-primary" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save preferences'}
        </button>
        {msg && <span className={`settings-msg ${msg === 'Saved.' ? 'ok' : 'fail'}`}>{msg}</span>}
      </div>
    </div>
  );
}

// ── Team section ───────────────────────────────────────────────────────────
function TeamSection() {
  return (
    <div className="settings-section">
      <h3>Team</h3>
      <p className="settings-desc">Team management and role-based access is coming soon. You'll be able to invite members and control which views they can access.</p>
      <div className="settings-coming-soon">Coming soon</div>
    </div>
  );
}

// ── About ──────────────────────────────────────────────────────────────────
function AboutSection() {
  const [info, setInfo] = React.useState(null);

  React.useEffect(() => {
    // getBuildInfo() returns whatever was stored by initVersionChecker on load
    const cached = window.__appBuildInfo;
    if (cached) { setInfo(cached); return; }
    // Fallback: fetch directly
    fetch(`/version.json?_=${Date.now()}`, { cache: 'no-store' })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setInfo(d); })
      .catch(() => {});
  }, []);

  const buildDate = info?.built
    ? new Date(info.built).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '—';

  return (
    <div className="settings-section">
      <h3>About</h3>
      <div className="settings-about-row">
        <span className="settings-about-label">App</span>
        <span className="settings-about-value">Projectly</span>
      </div>
      <div className="settings-about-row">
        <span className="settings-about-label">Build date</span>
        <span className="settings-about-value">{buildDate}</span>
      </div>
      {info?.v && (
        <div className="settings-about-row">
          <span className="settings-about-label">Build ID</span>
          <span className="settings-about-value settings-about-mono">{info.v}</span>
        </div>
      )}
    </div>
  );
}

// ── Danger zone ────────────────────────────────────────────────────────────
// App Store guideline 5.1.1(v) and Google Play both require an in-app route to
// delete an account, not just deactivate it. The server refuses while the user
// still owns a workspace containing other people; it returns those workspaces
// so we can name them here.
function DeleteAccountSection() {
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [blocking, setBlocking] = useState(null);

  const armed = confirmText.trim().toUpperCase() === 'DELETE';

  const run = async () => {
    if (!armed || busy) return;
    setBusy(true); setError(''); setBlocking(null);
    try {
      await httpsCallable(functions, 'deleteAccount')({});
      // The credential is gone; drop the session so the app returns to sign-in.
      await auth.signOut().catch(() => {});
      window.location.reload();
    } catch (e) {
      if (e?.code === 'functions/failed-precondition') {
        setBlocking(e?.details?.workspaces ?? []);
      } else {
        setError(e?.message || 'Could not delete your account.');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="settings-section">
      <h3>Delete account</h3>
      <p className="settings-hint">
        Permanently deletes your account, your inbox and any workspace you own on
        your own. This cannot be undone.
      </p>

      {blocking && (
        <div className="settings-error">
          {blocking.length === 1 ? 'This workspace still has' : 'These workspaces still have'}
          {' '}other members, so deleting your account would take their work with it.
          Transfer ownership or remove the other members first:
          <ul>
            {blocking.map(w => <li key={w.id}>{w.name}</li>)}
          </ul>
        </div>
      )}
      {error && <div className="settings-error">{error}</div>}

      <label className="panel-label" htmlFor="delete-confirm">
        Type DELETE to confirm
      </label>
      <input
        id="delete-confirm"
        className="panel-input"
        value={confirmText}
        onChange={e => setConfirmText(e.target.value)}
        placeholder="DELETE"
        autoComplete="off"
      />
      <button
        className="btn btn-danger"
        disabled={!armed || busy}
        onClick={run}
      >
        {busy ? 'Deleting…' : 'Delete my account'}
      </button>
    </div>
  );
}

// ── Main ───────────────────────────────────────────────────────────────────
export default function SettingsView({ user, isAdmin }) {
  return (
    <div className="settings-view">
      <div className="content-header"><h2>Settings</h2></div>
      <ProfileSection user={user} />
      <AppearanceSection />
      <NotificationsSection user={user} />
      <AISection isAdmin={isAdmin} />
      {isAdmin && <SmsTestSection />}
      {isAdmin && <TestAppPanel />}
      <TeamSection />
      <AboutSection />
      <DeleteAccountSection />
    </div>
  );
}
